import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export const Panel = ({ title, children, testId, className = "" }) => (
  <section className={`rounded-2xl border border-clay bg-white p-6 ${className}`} data-testid={testId}>
    <h2 className="text-lg font-black text-forest">{title}</h2>
    <div className="mt-4">{children}</div>
  </section>
);

export const Empty = () => <p className="py-6 text-sm text-moss">No visits in this period yet.</p>;

export const StatCard = ({ label, data, testId }) => (
  <div className="rounded-2xl border border-clay bg-white p-6" data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-[0.18em] text-moss">{label}</p>
    <p className="mt-3 font-display text-5xl font-black text-forest" data-testid={`${testId}-visitors`}>{data?.visitors ?? "–"}</p>
    <p className="mt-1 text-sm font-semibold text-sage">visitors · {data?.views ?? 0} page views</p>
  </div>
);

const shortLabel = (l) => {
  const d = new Date(l.length === 7 ? `${l}-01T12:00` : `${l}T12:00`);
  return l.length === 7 ? d.toLocaleDateString([], { month: "short", year: "2-digit" }) : d.toLocaleDateString([], { month: "short", day: "numeric" });
};

export const TrafficChart = ({ series }) => (
  <div className="h-72" data-testid="analytics-chart">
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={series} margin={{ left: -20, right: 8, top: 8 }}>
        <defs>
          <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#284B31" stopOpacity={0.35} /><stop offset="100%" stopColor="#284B31" stopOpacity={0} /></linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#E7E1D6" vertical={false} />
        <XAxis dataKey="label" tickFormatter={shortLabel} tick={{ fontSize: 11, fill: "#6B7B6E" }} tickLine={false} axisLine={false} minTickGap={20} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6B7B6E" }} tickLine={false} axisLine={false} />
        <Tooltip labelFormatter={shortLabel} contentStyle={{ borderRadius: 12, border: "1px solid #E7E1D6" }} />
        <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#284B31" strokeWidth={2.5} fill="url(#gv)" />
        <Area type="monotone" dataKey="views" name="Page views" stroke="#B9A27A" strokeWidth={2} fill="none" />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

export const BarList = ({ rows, label, sub, testId }) => {
  if (!rows?.length) return <Empty />;
  const max = Math.max(...rows.map((r) => r.visitors), 1);
  return (
    <ul className="space-y-2" data-testid={testId}>
      {rows.map((r, i) => (
        <li key={i} className="relative overflow-hidden rounded-lg px-3 py-2" data-testid={`${testId}-row-${i}`}>
          <span className="absolute inset-y-0 left-0 rounded-lg bg-sage/20 transition-[width] duration-500" style={{ width: `${(r.visitors / max) * 100}%` }} />
          <div className="relative flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0 truncate font-semibold text-ink">{label(r)}{sub && <span className="ml-2 text-xs font-medium text-moss">{sub(r)}</span>}</span>
            <span className="shrink-0 font-bold text-forest">{r.visitors} <span className="text-xs font-medium text-moss">· {r.views} views</span></span>
          </div>
        </li>
      ))}
    </ul>
  );
};

export const Split = ({ rows, names = {}, testId }) => {
  if (!rows?.length) return <Empty />;
  const total = rows.reduce((a, r) => a + r.visitors, 0) || 1;
  return (
    <div className="space-y-3" data-testid={testId}>
      {rows.map((r) => (
        <div key={r.name} data-testid={`${testId}-${r.name}`}>
          <div className="flex justify-between text-sm font-semibold text-ink"><span>{names[r.name] || r.name}</span><span>{Math.round((r.visitors / total) * 100)}% · {r.visitors}</span></div>
          <div className="mt-1 h-2 rounded-full bg-linen"><div className="h-2 rounded-full bg-forest" style={{ width: `${(r.visitors / total) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
};
